import * as React from "react";

import {
  Button,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { IconSearch } from "@tabler/icons-react";
import { BookMarked } from "lucide-react";

import { CloseButton } from "./CloseButton";

type SavedPromptsProps = React.ComponentProps<"div">;

function SavedPromptsBase({
  className,
  children,
  ...props
}: SavedPromptsProps) {
  return (
    <div
      className={cn(
        "af-flex af-max-h-96 af-w-[756px] af-max-w-full af-flex-col af-gap-3",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export const SavedPromptsButton = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof Button>) => {
  return (
    <Button
      ref={ref}
      variant="outline"
      size="default"
      className={cn("af-overflow-x-hidden", className)}
      {...props}
    >
      <BookMarked className="af-h-4 af-w-4" />
      <span className="af-truncate">Saved Prompts</span>
    </Button>
  );
};
SavedPromptsButton.displayName = "SavedPromptsButton";

interface SavedPromptsHeaderProps extends React.ComponentProps<"div"> {
  onClose?: () => void;
}

const Header = ({
  ref,
  className,
  onClose,
  ...props
}: SavedPromptsHeaderProps) => {
  return (
    <div
      ref={ref}
      className={cn("af-flex af-items-center af-justify-between", className)}
      {...props}
    >
      <h3 className="af-m-0 af-font-semibold">Saved prompts</h3>
      <CloseButton onClick={onClose} />
    </div>
  );
};
Header.displayName = "SavedPrompts.Header";

interface SavedPromptsSerachProps extends Omit<
  React.ComponentProps<typeof InputGroupInput>,
  "value"
> {
  searchPhrase?: string;
  onSearchPhraseChange?: (value: string) => void;
}

const Search = ({
  ref,
  searchPhrase,
  onChange,
  onSearchPhraseChange: onSearch,
  ...props
}: SavedPromptsSerachProps) => {
  return (
    <InputGroup>
      <InputGroupInput
        ref={ref}
        type="search"
        placeholder="Search Prompts..."
        value={searchPhrase}
        onChange={(e) => {
          onChange?.(e);
          onSearch?.(e.target.value);
        }}
        {...props}
      />
      <InputGroupAddon>
        <IconSearch className="af-size-4" />
      </InputGroupAddon>
    </InputGroup>
  );
};
Search.displayName = "SavedPrompts.Search";

const List = ({ ref, className, ...props }: React.ComponentProps<"ul">) => {
  return (
    <ul
      ref={ref}
      className={cn(
        "af-flex af-list-none af-flex-col af-gap-1 af-overflow-y-auto af-overflow-x-hidden af-p-0",
        className,
      )}
      {...props}
    />
  );
};
List.displayName = "SavedPrompts.List";

interface SavedPromptsListItemProps extends Omit<
  React.ComponentProps<"li">,
  "onClick"
> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const ListItem = ({
  ref,
  className,
  onClick,
  children,
  ...props
}: SavedPromptsListItemProps) => {
  return (
    <li ref={ref} {...props}>
      <button
        onClick={onClick}
        className={cn(
          "af-text-foreground-muted af-w-full af-py-4 af-text-left af-text-sm hover:af-cursor-pointer hover:af-text-foreground",
          className,
        )}
      >
        {children}
      </button>
    </li>
  );
};
ListItem.displayName = "SavedPrompts.ListItem";

const EmptyState = ({
  ref,
  className,
  ...props
}: React.ComponentProps<"div">) => {
  return (
    <div
      ref={ref}
      className={cn(
        "af-text-foreground-muted af-text-center af-text-sm",
        className,
      )}
      {...props}
    />
  );
};
EmptyState.displayName = "SavedPrompts.EmptyState";

export const SavedPrompts = Object.assign(SavedPromptsBase, {
  Header,
  Search,
  List,
  ListItem,
  EmptyState,
});
